import App from 'next/app'
import React from 'react'
import { ApolloProvider, Query } from 'react-apollo'
import { ThemeProvider } from 'styled-components'
import TagManager from 'react-gtm-module'
import { withRouter } from 'next/router'
import get from 'lodash/get'
import Cookies from 'js-cookie'
import '@fortawesome/fontawesome-svg-core/styles.css'

import withApolloClient from 'lib/WithApolloClient'
import { theme, themeMapper } from 'utils/Theme'
import { GlobalFont } from 'utils/GlobalFonts'
import { STATUS_CODES, DOMAINS } from 'utils/Constants'
import { gqlEndpoint, siteDomain } from 'utils/CareguideDomains'
import getSentry from 'utils/Sentry'
import { GetGoogleTagManagerID } from 'utils/GoogleTagManager'
import SimpleErrorPage from 'components/error/SimpleErrorPage'
import Footer from 'components/Footer'
import { Header } from 'components/Header'
import SiteSEO from 'components/SEO/SiteSEO'
import SiteQuery from 'components/api/queries/SiteQuery.gql'
import LoggedInQuery from 'components/api/queries/LoggedInQuery.gql'
import AutoLoginMutation from 'components/api/mutations/AutoLoginMutation.gql'
import SentryErrorReportingPage from 'components/SentryErrorReportingPage'
import { Group } from 'utils/Analytics'
import { getEnvironment } from 'utils/Environments'

const { captureException } = getSentry()

function getLocation(req) {
  if (req) {
    const fullUrl = 'https://' + req.headers.host + req.url
    const urlObject = new URL(fullUrl)
    return {
      pathname: urlObject.pathname,
      query: urlObject.search,
      url: req.url,
    }
  }

  return {
    pathname: window.location.pathname,
    query: window.location.search,
    url: window.location.pathname + window.location.search,
  }
}

class MyApp extends App {
  state = {
    hasError: false,
    errorEventId: undefined,
    navBarOpen: false,
    loggedIn: false,
    loggedInHeaderData: null,
    userData: null,
  }

  hasClosedBanner = Cookies.get(this.HIDE_COVID_BANNER)

  static async getInitialProps({ _component, ctx }) {
    try {
      const { req, res } = ctx
      const location = getLocation(req)
      const { CODE301, CODE404 } = STATUS_CODES
      const errorCode = res && res.statusCode > 200 ? res.statusCode : null

      if (location && location.url && location.url && location.url.includes('/near')) {
        if (req && res && req.url) {
          res.writeHead(CODE301, { Location: location.url.replace('/near', '') })
          res.end()
        }
      }

      if (errorCode === CODE404) {
        if (typeof window === 'undefined') {
          /**
           * Replaces slashes
           * Test vectors:
           * `/test/test/` -> `/test/test`
           * `/test/////test////` -> `/test/test`
           * `/test//test//?a=1&b=2` -> `/test?a=1&b=2`
           * `/test///#test` -> `/test#test`
           */
          const correctPath = (invalidPath) =>
            invalidPath
              .replace(/\/+$/, '')
              .replace(/\/+#/, '#')
              .replace(/\/+\?/, '?')
              .replace(/\/+/g, '/')
          // Do a 301 Permenant redirect to correct path.
          if (req && res && req.url && correctPath(req.url) !== req.url) {
            res.writeHead(CODE301, { Location: correctPath(req.url) })
            res.end()
          }
        }
      }
      const environment = getEnvironment(ctx)
      const pageProps = {
        domain: siteDomain(ctx),
        pathname: location.pathname,
        q: location.query,
        reqURL: location.url,
        gqlEndpoint: gqlEndpoint(ctx, environment),
        environment: environment,
        errorCode,
      }
      return { pageProps }
    } catch (error) {
      // Capture errors that happen during a page's getInitialProps; work both client and server-side.
      const errorEventId = captureException(error, ctx)
      return {
        hasError: true,
        errorEventId,
      }
    }
  }

  // Allows for rendering the Sentry report error UI as a fallback.
  static getDerivedStateFromError() {
    return { hasError: true }
  }

  /**
   *
   * @description Catch and log capture client-side exceptions to Sentry
   * @param {*} error
   * @param {Object} errorInfo
   */
  async componentDidCatch(error, errorInfo) {
    const errorEventId = captureException(error, { errorInfo })
    // Store error in state; used to conditionally render Sentry error reporting UI.
    this.setState({ errorEventId })
    super.componentDidCatch(error, errorInfo)
  }

  // TODO: Avoid flash of content on componentDidMount for log in by showing spinner at first and using cookie
  async componentDidMount() {
    this.login()
    const tagManagerArgs = {
      gtmId: GetGoogleTagManagerID(),
    }
    TagManager.initialize(tagManagerArgs)
  }

  async login() {
    const { apolloClient, router } = this.props
    const res = await apolloClient.query({ query: LoggedInQuery })
    const viewer = get(res, 'data.viewer')
    if (!viewer) {
      return
    }

    const user = viewer.user
    const auto_login_token = get(router, 'query.altkn')

    if (user) {
      this.setLoggedInUserState(res.data, user)
    } else if (auto_login_token) {
      this.autoLogin(auto_login_token)
    } else {
      this.setLoggedInUserState(res.data, null)
    }
  }

  setLoggedInUserState(data, user) {
    if (!data) {
      return
    }

    const loggedIn = Boolean(user)

    this.updateOptimize(loggedIn)

    this.setState({
      loggedIn: loggedIn,
      loggedInHeaderData: data,
      userData: user,
    })
  }

  async updateOptimize(loggedIn) {
    if (!window) {
      return
    }

    Cookies.set('cgc_optimize_logged_in', loggedIn)

    if (window.dataLayer) {
      await window.dataLayer.push({ event: 'optimize.activate.login_flow_completed' })
    }

    if (window.gtag) {
      window.gtag('event', 'optimize.callback', {
        callback: (value, name) => Group(name, { variant: value }),
      })
    }
  }

  async autoLogin(auto_login_token) {
    const { apolloClient } = this.props

    const res = await apolloClient.mutate({
      mutation: AutoLoginMutation,
      variables: { auto_login_token: auto_login_token },
    })
    const user = get(res, 'data.viewer.user')

    this.setLoggedInUserState(res.data, user)
  }

  toggleNavBarOpen = () => {
    this.setState((prevState) => ({ navBarOpen: !prevState.navBarOpen }))
  }

  hideCovidBanner = () => {
    Cookies.set(this.HIDE_COVID_BANNER, 'true', { expires: 7 })
  }

  redirectToArticle = () => {
    this.hideCovidBanner()
    window.location.assign('https://help.careguide.com/hc/en-us/articles/360041264611')
  }

  render() {
    const {
      Component,
      pageProps: { domain },
      pageProps,
      apolloClient,
    } = this.props
    const { navBarOpen, errorEventId, hasError, loggedIn, loggedInHeaderData } = this.state
    const { CARE_GUIDE } = DOMAINS

    if (hasError) {
      return <SentryErrorReportingPage errorEventId={errorEventId} />
    }

    if (domain === CARE_GUIDE) {
      return this.renderCareGuideCorp()
    }
    return (
      <ApolloProvider client={apolloClient}>
        <Query query={SiteQuery} variables={{ domain: domain }}>
          {({ loading, error, data }) => {
            if (error) {
              return <SimpleErrorPage />
            }

            if (!data || !data.site) {
              return null
            }

            const site = get(data, 'site')
            const palette = get(site, 'palette')
            const assets = get(site, 'assets')

            const metaDescription = get(assets, 'metaDescription')
            const title = get(assets, 'title')
            const favicon = get(assets, 'favicon')
            const viewer = get(loggedInHeaderData, 'viewer')

            const cleanTheme = themeMapper(palette)
            const mergedColors = Object.assign(theme.colors, cleanTheme.colors)
            theme.colors = mergedColors

            return (
              <>
                <SiteSEO faviconLink={favicon} domain={domain} />
                <ThemeProvider theme={theme}>
                  <Header
                    domain={domain}
                    loggedIn={loggedIn}
                    viewer={viewer}
                    site={site}
                    show={navBarOpen}
                    handleNavClick={this.toggleNavBarOpen}
                  />
                  <GlobalFont />
                  {!navBarOpen && (
                    <>
                      <Component
                        pageProps={pageProps}
                        metaDescription={metaDescription}
                        title={title}
                        loggedIn={loggedIn}
                        viewer={viewer}
                        apolloClient={apolloClient}
                      />
                      <Footer domain={domain} site={site} />
                    </>
                  )}
                </ThemeProvider>
              </>
            )
          }}
        </Query>
      </ApolloProvider>
    )
  }

  renderCareGuideCorp() {
    const {
      Component,
      pageProps: { domain },
      pageProps,
      apolloClient,
    } = this.props
    return (
      <ApolloProvider client={apolloClient}>
        <Query query={SiteQuery} variables={{ domain: domain }}>
          {({ loading, error, data }) => {
            if (error) return <SimpleErrorPage />
            if (loading) return null
            const favicon = get(data, 'site.assets.favicon')

            return (
              <>
                <SiteSEO faviconLink={favicon} domain={domain} />
                <Component pageProps={pageProps} />
              </>
            )
          }}
        </Query>
      </ApolloProvider>
    )
  }
}

export default withRouter(withApolloClient(MyApp))
